
import { Search, X, SlidersHorizontal } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { cn } from '../../lib/utils';

export interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  onClear?: () => void;
  showClearButton?: boolean;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  // Filters functionality
  onFiltersClick?: () => void;
  showFiltersIcon?: boolean;
  filtersActive?: boolean;
  id?: string;
  // Debouncing
  debounceMs?: number; // Delay in ms before calling onChange (0 = no debounce)
}

export function SearchInput({
  value,
  onChange,
  placeholder = "Search...",
  className,
  onClear,
  showClearButton = true,
  disabled = false,
  size = 'md',
  onFiltersClick,
  showFiltersIcon = false,
  filtersActive = false,
  id,
  debounceMs = 500 // Default 500ms debounce
}: SearchInputProps) {
  // Internal state for immediate UI updates
  const [internalValue, setInternalValue] = useState(value);
  const debounceTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);

  // The last value this box handed out — through onChange as the timer ended,
  // or through Clear. `value` coming back equal to it is our own change landing.
  const emittedRef = useRef<string | undefined>(undefined);

  // Sync internal value when external value changes (e.g., from URL or clear button).
  // A change from outside — a "Clear all", a filter chip, Back — wins: drop the
  // pending text, or it lands half a second later and undoes it. Our own change
  // landing is not "outside", and must not: a parent that stores the value in
  // the URL commits it as a transition, some time after onChange fired, and a
  // keystroke typed in that gap would be erased from the box along with its
  // timer — 'ana' landing wiped the 'b' just typed after it.
  useEffect(() => {
    if (value === emittedRef.current) return;
    emittedRef.current = undefined;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = undefined;
    }
    setInternalValue(value);
  }, [value]);

  // Handle input change with debouncing
  const handleInputChange = (newValue: string) => {
    // Update UI immediately
    setInternalValue(newValue);

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer to call onChange after debounce delay
    if (debounceMs > 0) {
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = undefined;
        emittedRef.current = newValue;
        onChange(newValue);
      }, debounceMs);
    } else {
      // No debounce - call immediately
      emittedRef.current = newValue;
      onChange(newValue);
    }
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const sizeStyles = {
    sm: {
      height: '32px',
      padding: 'pl-8 pr-3 py-1.5',
      iconSize: 'h-3 w-3',
      iconLeft: 'left-2.5',
      clearRight: 'right-2',
      filtersRight: 'right-2'
    },
    md: {
      height: '38px',
      padding: 'pl-10 pr-4 py-2',
      iconSize: 'h-4 w-4',
      iconLeft: 'left-3',
      clearRight: 'right-3',
      filtersRight: 'right-3'
    },
    lg: {
      height: '44px',
      padding: 'pl-12 pr-5 py-3',
      iconSize: 'h-5 w-5',
      iconLeft: 'left-3.5',
      clearRight: 'right-3.5',
      filtersRight: 'right-3.5'
    }
  };

  const styles = sizeStyles[size];
  const hasClearButton = showClearButton && internalValue && onClear;
  const hasFiltersIcon = showFiltersIcon && onFiltersClick;
  
  // Calculate right padding based on icons present
  let rightPadding = '';
  if (hasClearButton && hasFiltersIcon) {
    // Both clear and filters icons - need more space
    rightPadding = size === 'sm' ? 'pr-16' : size === 'md' ? 'pr-20' : 'pr-24';
  } else if (hasClearButton || hasFiltersIcon) {
    // Only one icon - standard space
    rightPadding = size === 'sm' ? 'pr-8' : size === 'md' ? 'pr-10' : 'pr-12';
  }

  return (
    <div className={cn('relative', className)}>
      <Search 
        className={cn(
          'absolute top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none',
          styles.iconSize,
          styles.iconLeft
        )} 
      />
      <input
        id={id}
        type="text"
        placeholder={placeholder}
        value={internalValue}
        onChange={(e) => handleInputChange(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        disabled={disabled}
        className={cn(
          'w-full border border-field rounded-md bg-background dark:bg-white/5 text-gray-900 dark:text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent field-text transition-colors',
          styles.padding,
          rightPadding,
          disabled && 'opacity-50 cursor-not-allowed'
        )}
        style={{ height: styles.height }}
      />
      {/* Clear Button */}
      {hasClearButton && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            // Cancel the pending debounce, or the text typed just before Clear
            // is applied half a second after it. And empty the box here: when
            // the parent's value is already '' its onClear changes nothing, so
            // the sync-from-`value` effect never runs to empty it.
            if (debounceTimerRef.current) {
              clearTimeout(debounceTimerRef.current);
              debounceTimerRef.current = undefined;
            }
            setInternalValue('');
            emittedRef.current = '';
            onClear?.();
          }}
          className={cn(
            'absolute top-1/2 transform -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/10 transition-colors',
            hasFiltersIcon
              ? (size === 'sm' ? 'right-8' : size === 'md' ? 'right-10' : 'right-12')
              : styles.clearRight
          )}
          type="button"
          id={id ? `${id}-clear` : 'search-clear'}
          name={id ? `${id}-clear` : 'search-clear'}
        >
          <X className={styles.iconSize} />
        </button>
      )}
      
      {/* Filters Icon */}
      {hasFiltersIcon && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onFiltersClick?.();
          }}
          className={cn(
            'absolute top-1/2 transform -translate-y-1/2 transition-colors',
            filtersActive 
              ? 'text-primary' 
              : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300',
            styles.filtersRight
          )}
          type="button"
          id={id ? `${id}-filters` : 'search-filters'}
          name={id ? `${id}-filters` : 'search-filters'}
        >
          <SlidersHorizontal className={cn(styles.iconSize)} />
        </button>
      )}
    </div>
  );
} 